import React from 'react';
import { Badge, Box, Flex, TextV2 } from '@withjoy/joykit';

type FilterOptionLabelProps = Readonly<{
  label: string;
  /** Designs this option matches, under the rest of the current selection. */
  count: number;
  /** Omit to inherit the surrounding row's typography (the mobile drawer does). */
  typographyVariant?: 'body1' | 'button1';
  /** Colour swatch rendered before the label, where the facet has one. */
  swatch?: React.ReactNode;
}>;

/**
 * A filter option's label with its match count as a neutral counter badge pushed to the right
 * edge of the row (Nick, 2026-10-17: "The counts should be using a style like this… the
 * component is in Joykit, it's called badge", "if you right align them, it will make the view
 * look calmer") — rather than plain text in parentheses.
 *
 * The row's checkbox must hand its label slot the full remaining width for the badge to reach
 * the right edge; spread `FILTER_OPTION_LABEL_PROPS` into the `Label` override to do that.
 */
export const FilterOptionLabel = ({ label, count, typographyVariant, swatch }: FilterOptionLabelProps) => (
  <Flex
    alignItems="center"
    justifyContent="space-between"
    gap={3}
    width="100%"
  >
    <Flex
      alignItems="center"
      gap={2}
      minWidth={0}
    >
      {swatch}
      {typographyVariant ? <TextV2 typographyVariant={typographyVariant}>{label}</TextV2> : <Box as="span">{label}</Box>}
    </Flex>
    <Badge
      variant="counter"
      intent="neutral"
      size="sm"
      count={count}
    />
  </Flex>
);

/** `Label` override props that let {@link FilterOptionLabel} fill its row. */
export const FILTER_OPTION_LABEL_PROPS = { flex: 1, minWidth: 0 } as const;
